import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Compass, Sparkles, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UI, t } from "@/i18n/translations";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TagInput } from "@/components/TagInput";
import { LanguageSelect } from "@/components/LanguageSelect";

const TODAY = new Date().toISOString().split("T")[0];

export function ProfileSheet({ open, onClose }) {
  const { lang } = useLang();
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  React.useEffect(() => {
    if (open && user) {
      setForm({
        prenom: user.prenom || "",
        date_arrivee_en_france: user.date_arrivee_en_france || "",
        ecole: user.ecole || "",
        ville: user.ville || "",
        quartier: user.quartier || "",
        langues_parlees: user.langues_parlees || [],
        centres_interet: user.centres_interet || [],
      });
    }
  }, [open, user]);

  if (!open || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success(lang === "fr" ? "Profil enregistré" : "Profile saved");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || UI.common.error[lang]);
    } finally {
      setSaving(false);
    }
  };

  const leaveCommunity = async () => {
    try {
      await api.post("/community/leave");
      setUser({ ...user, community_member: false });
      toast.success(t(UI.communityMod.left, lang));
    } catch {
      toast.error(UI.common.error[lang]);
    }
  };

  const replayOnboarding = async () => {
    try {
      const { data } = await api.post("/user/state", { onboarding_completed: false });
      setUser(data);
      onClose();
    } catch {
      toast.error(UI.common.error[lang]);
    }
  };

  const A = UI.auth;
  const status = user.buddy_eligible ? t(UI.status.buddy, lang) : t(UI.status.nouvel, lang);

  return (
    <div className="fixed inset-0 z-50" data-testid="profile-sheet">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-paper shadow-2xl overflow-y-auto animate-fade-up">
        <div className="sticky top-0 bg-paper/90 backdrop-blur-xl border-b border-line px-5 h-16 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">{t(UI.common.profile, lang)}</h2>
          <button onClick={onClose} data-testid="close-profile-btn" className="grid place-items-center w-9 h-9 rounded-full border border-line bg-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-[18px] bg-ink text-paper p-5 flex items-center gap-4">
            <div className="grid place-items-center w-12 h-12 rounded-full bg-amber/20">
              <Compass className="w-6 h-6 text-amber" />
            </div>
            <div>
              <div className="font-display text-lg font-bold">{user.prenom}</div>
              <div className="text-sm text-paper/70">{user.email}</div>
              <div className="mt-1 inline-flex items-center gap-2 text-xs font-semibold">
                <span className="rounded-full bg-sage/25 text-paper px-2 py-0.5">{status}</span>
                {user.mois_en_france != null && (
                  <span className="text-paper/60">{user.mois_en_france} {t(UI.status.monthsIn, lang)}</span>
                )}
              </div>
            </div>
          </div>

          <Field label={t(A.prenom, lang)}><input data-testid="profile-prenom" maxLength={60} className={inputCls} value={form.prenom} onChange={(e) => set("prenom", e.target.value)} /></Field>
          <Field label={t(A.dateArrivee, lang)}><input type="date" max={TODAY} data-testid="profile-date" className={inputCls} value={form.date_arrivee_en_france || ""} onChange={(e) => set("date_arrivee_en_france", e.target.value)} /></Field>
          <Field label={t(A.ecole, lang)}><input data-testid="profile-ecole" maxLength={120} className={inputCls} value={form.ecole} onChange={(e) => set("ecole", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t(A.ville, lang)}><input data-testid="profile-ville" maxLength={60} className={inputCls} value={form.ville} onChange={(e) => set("ville", e.target.value)} /></Field>
            <Field label={t(A.quartier, lang)}><input data-testid="profile-quartier" maxLength={60} className={inputCls} value={form.quartier} onChange={(e) => set("quartier", e.target.value)} /></Field>
          </div>
          <Field label={t(A.langues, lang)}><LanguageSelect testId="profile-langues" value={form.langues_parlees} onChange={(v) => set("langues_parlees", v)} /></Field>
          <Field label={t(A.interets, lang)}><TagInput testId="profile-interets" value={form.centres_interet} onChange={(v) => set("centres_interet", v)} placeholder={t(A.addTag, lang)} /></Field>

          <button
            data-testid="save-profile-btn"
            onClick={save}
            disabled={saving}
            className="w-full rounded-2xl bg-amber text-ink font-bold py-3.5 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60"
          >
            {saving ? "…" : t(A.save, lang)}
          </button>

          {user.community_member && (
            <div className="pt-2">
              <div className="flex items-center gap-2 text-sm text-sage font-semibold mb-2">
                <Compass className="w-4 h-4" /> {t(UI.communityMod.memberBadge, lang)} · Providence
              </div>
              <button
                data-testid="leave-community-btn"
                onClick={leaveCommunity}
                className="w-full rounded-2xl border border-coral/40 text-coral font-semibold py-3 text-sm hover:bg-coral/8 transition-colors"
              >
                {t(UI.communityMod.leaveCommunity, lang)}
              </button>
            </div>
          )}

          <div className="pt-4 mt-2 border-t border-line">
            <div className="text-sm font-semibold text-ink/60 mb-2">{t(UI.settings.help, lang)}</div>
            <div className="space-y-2">
              <button data-testid="replay-onboarding-btn" onClick={replayOnboarding} className="w-full text-left rounded-2xl border border-line bg-white px-4 py-3 text-sm font-medium hover:border-amber transition-colors flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber" /> {t(UI.settings.replayOnboarding, lang)}
              </button>
              <Link to="/legal/terms" data-testid="settings-terms" onClick={onClose} className="block rounded-2xl border border-line bg-white px-4 py-3 text-sm font-medium hover:border-amber transition-colors flex items-center gap-2">
                <FileText className="w-4 h-4 text-ink/50" /> {t(UI.legal.terms, lang)}
              </Link>
              <Link to="/legal/privacy" data-testid="settings-privacy" onClick={onClose} className="block rounded-2xl border border-line bg-white px-4 py-3 text-sm font-medium hover:border-amber transition-colors flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-ink/50" /> {t(UI.legal.privacy, lang)}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-2xl border border-line bg-white px-3.5 py-3 text-sm outline-none focus:border-amber transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-1.5 text-ink/80">{label}</span>
      {children}
    </label>
  );
}
